import { Subscription } from 'rxjs';
import { CampanhaSearch } from '../../campanha-search';
import { CampanhaSearchViewStatus } from '../../campanha-search-view-status';
import { isStatusEmAnalise, isStatusEmAndamento, isStatusEmRevisao, isStatusEncerrada, isStatusNaoConfirmada, isStatusReprovada } from '../../status';
import { CampanhaPipelineListTotal } from './campanha-pipeline-list-total';

export class CampanhaPipelineList {
    id: string;
    title: string;
    color: string;
    icon: string;
    isCollapsed = false;
    isCardsCollapsesOpening: boolean;
    isCardsCollapsesOpened: boolean;

    /** Quantidade limite de campanhas que devem ser retornados nas pesquisa sob-demanda */
    private limit: number;
    /** Se atingiu o fim da pesquisa sob-demanda das campanhas na list */
    done: boolean;

    // Statuses
    status: CampanhaSearchViewStatus;
    isLoadingStatus: boolean;
    getStatusMessageError: string;
    isStatusNaoConfirmada: boolean;
    isStatusEmAnalise: boolean;
    isStatusReprovada: boolean;
    isStatusEmAndamento: boolean;
    isStatusEmRevisao: boolean;
    isStatusEncerrada: boolean;

    // Entities
    entities: CampanhaSearch[];
    private idsEntitiesToSearch: number[];
    isLoading: boolean;
    searchMessageError: string;

    // Total
    total: CampanhaPipelineListTotal;

    currentSub$: Subscription;

    constructor(status: CampanhaSearchViewStatus, limit: number) {
        if (!status) {
            throw new Error('Não foi possível criar a lista para o pipeline por que nenhum status foi informado');
        }

        this.id = status.id.toString();
        this.title = status.nome;
        this.color = status.cor;
        this.icon = status.icon;
        this.entities = [];
        this.idsEntitiesToSearch = [];
        this.limit = limit;
        this.idsEntitiesToSearch = status.idsCampanhas ?? [];
        this.total = new CampanhaPipelineListTotal(status.total);

        this.status = status;
        if (isStatusNaoConfirmada(status.id)) { // Não Confirmada
            this.isStatusNaoConfirmada = true;
            this.searchMessageError = 'Erro ao pesquisar as campanhas não confirmadas';
            this.getStatusMessageError = 'Erro ao obter os totais das campanhas não confirmadas';
        } else if (isStatusEmAnalise(status.id)) { // Em Análise
            this.isStatusEmAnalise = true;
            this.searchMessageError = 'Erro ao pesquisar as campanhas em análise';
            this.getStatusMessageError = 'Erro ao obter os totais das campanhas em análise';
        } else if (isStatusReprovada(status.id)) { // Reprovada
            this.isStatusReprovada = true;
            this.searchMessageError = 'Erro ao pesquisar as campanhas reprovadas';
            this.getStatusMessageError = 'Erro ao obter os totais das campanhas reprovadas';
        } else if (isStatusEmAndamento(status.id)) { // Em Andamento
            this.isStatusEmAndamento = true;
            this.searchMessageError = 'Erro ao pesquisar as campanhas em andamento';
            this.getStatusMessageError = 'Erro ao obter os totais das campanhas em andamento';
        } else if (isStatusEmRevisao(status.id)) { // Em Revisão
            this.isStatusEmRevisao = true;
            this.searchMessageError = 'Erro ao pesquisar as campanhas em revisão';
            this.getStatusMessageError = 'Erro ao obter os totais das campanhas em revisão';
        } else if (isStatusEncerrada(status.id)) { // Encerreda
            this.isStatusEncerrada = true;
            this.searchMessageError = 'Erro ao pesquisar as campanhas encerradas';
            this.getStatusMessageError = 'Erro ao obter os totais das campanhas encerradas';
        }

        this.isCardsCollapsesOpened = false;
    }

    /** Adiciona as campanhas informadas na list */
    addEntities(entities: CampanhaSearch[]) {
        if (!entities || entities.length === 0) {
            return;
        }

        entities.forEach(entity => {
            this.entities.push(entity);
        });

        // Verificando se atingiu o fim da pesquisa quando a quantidade de campanhas já adicionadas na list for igual ou maior que a quantidade de campanhas
        this.done = this.entities.length >= this.total.quantidadeCampanhas;
    }

    /** Obtem os IDs das próximas campanhas para serem pesquisados */
    getMoreEntities(): number[] {
        const starIndex = this.entities.length;
        let endIndex: number;
        if ((starIndex + this.limit) <= this.total.quantidadeCampanhas) {
            endIndex = starIndex + this.limit;
        } else {
            endIndex = undefined;
        }

        return this.idsEntitiesToSearch.slice(starIndex, endIndex);
    }

    /** Obtem o index da campanha */
    getIndexEntity(id: number): number {
        return this.entities?.findIndex(entityFilter => entityFilter.id === id);
    }

    /** Verifica se a campanha existe na list */
    entityExists(id: number): boolean {
        const index = this.getIndexEntity(id);
        return index > -1;
    }

    /** Obtem a campanha */
    getEntity(id: number): CampanhaSearch {
        return this.entities?.find(entityFilter => entityFilter.id === id);
    }

    /** Adiciona uma campanha */
    addOneEntity(entity: CampanhaSearch, roiPontoEquilibrio: number) {
        if (!entity) {
            return;
        }
        this.entities.unshift(entity);
        this.total.addEntity(entity, roiPontoEquilibrio);
    }

    /** Atualiza a campanha informada */
    updateOneEntity(entityNew: CampanhaSearch, entityOld: CampanhaSearch, roiPontoEquilibrio: number) {
        if (!entityNew || !this.entities) {
            return;
        }

        const index = this.getIndexEntity(entityNew.id);
        if (index < 0) {
            return;
        }
        const entityAtual = this.entities[index];

        // Primeiro removemos os valores atuais da campanha do total
        this.total.removeEntity(entityOld ? entityOld : entityNew, roiPontoEquilibrio);

        // Depois atualizamos ou inserimos a campanha e adicionamos os seus novos valores no total
        entityNew.pipelineCard = entityAtual.pipelineCard;
        this.entities[index] = entityNew;
        this.total.addEntity(entityNew, roiPontoEquilibrio);
    }

    /** Remove a campanha informada */
    removeOneEntity(id: number, roiPontoEquilibrio: number) {
        const index = this.getIndexEntity(id);
        if (index < 0) {
            return;
        }

        const entity = this.entities[index];
        this.entities.splice(index, 1);
        this.total.removeEntity(entity, roiPontoEquilibrio);
    }
}
